import {
  Banknote,
  Coins,
  CreditCard,
  HandCoins,
  Receipt,
  ShoppingBag,
  Smartphone,
  Wallet,
} from "lucide-react";
import { cn, displayPrice, getPaymentMethodLabel } from "@/lib/utils";
import LabelHint from "@/components/ui/LabelHint";
import { SalesReport, PaymentMethodName } from "@/redux/services/pos/pos.type";
import { useGetTimeAnalyticsQuery } from "@/redux/services/dashboard/dashboard.api";
import { computeDelta, Delta, DeltaText } from "./reportDelta";
import { reportCard, reportInnerCard } from "./reportCard";

interface TotalCollectedSectionProps {
  report: SalesReport;
  previousReport?: SalesReport;
  currency: string;
  restaurantId: string;
  startDate: string;
  endDate: string;
}

/** Minimal SVG sparkline — a relative trend line, no axes. */
function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;
  const width = 100;
  const height = 24;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data
    .map((value, index) => {
      const x = (index / (data.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="mt-3 h-6 w-full"
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

const PAYMENT_METHOD_ICONS: Record<string, React.ElementType> = {
  cash: Banknote,
  card: CreditCard,
  "mobile money": Smartphone,
  wave: Smartphone,
  "orange money": Smartphone,
};

function TotalCollectedSection({
  report,
  previousReport,
  currency,
  restaurantId,
  startDate,
  endDate,
}: TotalCollectedSectionProps) {
  // Per-day series for the card sparklines. Shares the time-analytics query with
  // the charts/peak-times sections, so RTK Query serves it from cache.
  const { data: timeData } = useGetTimeAnalyticsQuery(
    {
      restaurant_id: restaurantId,
      start_date: startDate,
      end_date: endDate,
      compare: true,
    },
    { skip: !restaurantId },
  );
  const dailyTrends = timeData?.data.current.daily_trends ?? [];
  // "Total encaissé" plots money collected (successful payments), the same
  // basis as the figure above it; `revenue` is the billed value (SFAPI-115).
  const revenueSpark = dailyTrends.map((day) => day.collected ?? 0);
  const ordersSpark = dailyTrends.map((day) => day.orders);
  // "Panier moyen" follows its card (the report's averageOrderValue): the
  // successful payments of fully paid orders divided by their count, per
  // bucket. Older API payloads without those fields fall back to the billed
  // average (SFAPI-115).
  const basketSpark = dailyTrends.map((day) => {
    if (day.paid_orders !== undefined && day.settled_revenue !== undefined) {
      return day.paid_orders > 0 ? day.settled_revenue / day.paid_orders : 0;
    }
    return day.orders > 0 ? day.revenue / day.orders : 0;
  });

  const {
    actualTotalRevenue,
    expectedTotalRevenue,
    revenueByPaymentMethods,
    tipsByPaymentMethods,
    paidOrders,
    totalOrders,
    totalPaymentTips,
    totalTipMovements,
  } = report;

  const totalTips = totalPaymentTips + totalTipMovements;
  const prevTips = previousReport
    ? previousReport.totalPaymentTips + previousReport.totalTipMovements
    : undefined;

  const methodEntries = Object.entries(revenueByPaymentMethods).sort(
    (a, b) => b[1] - a[1]
  );

  const metrics = [
    {
      label: "Total encaissé",
      hintTooltip:
        "Argent réellement encaissé sur la période, acomptes sur commandes partiellement payées inclus. « Ventes facturées » = valeur totale des commandes valides, soldes non encore encaissés compris. L'argent encaissé sur des commandes annulées apparaît uniquement dans « Revenus ».",
      value: displayPrice(actualTotalRevenue, currency),
      hint: `sur ${displayPrice(expectedTotalRevenue, currency)} de ventes facturées`,
      icon: Wallet,
      accent: "#066a69",
      delta: computeDelta(actualTotalRevenue, previousReport?.actualTotalRevenue),
      sparkline: revenueSpark,
    },
    {
      label: "Commandes",
      value: String(totalOrders),
      hint:
        totalOrders === 0
          ? "aucune commande"
          : `${paidOrders} payée${paidOrders > 1 ? "s" : ""}`,
      icon: ShoppingBag,
      accent: "#f0590e",
      delta: computeDelta(totalOrders, previousReport?.totalOrders),
      sparkline: ordersSpark,
    },
    {
      label: "Panier moyen",
      value:
        paidOrders > 0 ? displayPrice(report.averageOrderValue, currency) : "—",
      hint: paidOrders > 0 ? "sur les commandes payées" : "aucune payée",
      icon: Receipt,
      accent: "#6366f1",
      delta:
        paidOrders > 0
          ? computeDelta(
              report.averageOrderValue,
              previousReport?.averageOrderValue
            )
          : null,
      sparkline: basketSpark,
    },
    {
      label: "Pourboires",
      value: displayPrice(totalTips, currency),
      hint:
        totalTips === 0
          ? "aucun pourboire"
          : `${displayPrice(totalPaymentTips, currency)} au paiement · ${displayPrice(totalTipMovements, currency)} en caisse`,
      icon: HandCoins,
      accent: "#f59e0b",
      delta: computeDelta(totalTips, prevTips),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => (
          <Metric key={metric.label} {...metric} />
        ))}
      </div>

      <section className={cn(reportCard, "p-6")}>
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Encaissements par moyen de paiement
        </h2>
        {methodEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun paiement enregistré sur cette période.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {methodEntries.map(([method, amount]) => {
              const Icon = PAYMENT_METHOD_ICONS[method] ?? Banknote;
              const share =
                actualTotalRevenue > 0 ? (amount / actualTotalRevenue) * 100 : 0;
              const methodTip = tipsByPaymentMethods?.[method] ?? 0;
              const methodDelta = computeDelta(
                amount,
                previousReport?.revenueByPaymentMethods?.[method],
              );
              return (
                <div key={method} className={cn(reportInnerCard, "p-4")}>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium capitalize text-card-foreground">
                      {getPaymentMethodLabel(method as PaymentMethodName)}
                    </span>
                  </div>
                  <p className="text-xl font-semibold tabular-nums text-card-foreground">
                    {displayPrice(amount, currency)}
                  </p>
                  {/* A slim share bar makes the split legible at a glance. */}
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${Math.min(share, 100)}%` }}
                    />
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      {share.toFixed(0)}% du total
                    </p>
                    <DeltaText delta={methodDelta} className="text-xs" />
                  </div>
                  {methodTip > 0 && (
                    <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-accent">
                      <Coins className="h-3.5 w-3.5 shrink-0" />
                      <span>{displayPrice(methodTip, currency)} de pourboire</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function Metric({
  label,
  hintTooltip,
  value,
  hint,
  icon: Icon,
  accent,
  delta,
  sparkline,
}: {
  label: string;
  hintTooltip?: string;
  value: string;
  hint: string;
  icon: React.ElementType;
  accent: string;
  delta: Delta;
  sparkline?: number[];
}) {
  return (
    <div className={cn("relative overflow-hidden p-5", reportCard)}>
      {/* Colored top rule ties the card to its metric family. */}
      <span
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: accent }}
      />
      <div
        className="flex h-9 w-9 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${accent}1a` }}
      >
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <p className="text-2xl font-semibold tabular-nums text-card-foreground">
          {value}
        </p>
        <DeltaText delta={delta} className="text-sm" />
      </div>
      <p className="mt-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
        {hintTooltip && <LabelHint text={hintTooltip} />}
      </p>
      <p className="mt-1 truncate text-xs text-muted-foreground">{hint}</p>
      {sparkline && <Sparkline data={sparkline} color={accent} />}
    </div>
  );
}

export default TotalCollectedSection;
